import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import React from "react";
import { firstPostDestination } from "../lib/takeover";
import { readTakeoverFlags } from "../lib/takeover-flags";
import { useOnboarding } from "./useOnboarding";

/** Sends a location still waiting on its first post to the takeover, or to the post form while the takeover is off. */
export const useFirstPostRedirect = (isInitialGigPost: boolean, locationId: number | undefined) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { gateWith } = useOnboarding(locationId);
  // Read at mount: useStripeRedirectBillingUpdate clears these params before a later render could see them.
  const [isStripeReturn] = React.useState(() => {
    const params = new URLSearchParams(globalThis.location.search);
    return params.has("redirect_status") || params.has("setup_intent");
  });
  const redirectedFor = React.useRef<number | null>(null);

  React.useEffect(() => {
    // Don't redirect away from the Stripe payment setup return flow.
    if (!isInitialGigPost || locationId === undefined || isStripeReturn) return;
    // Once per location: a flag, the window width or a skip changing afterwards must not move the manager again.
    if (redirectedFor.current === locationId) return;
    redirectedFor.current = locationId;
    const params = { locationId: String(locationId) };
    void readTakeoverFlags(queryClient).then((flags) => {
      const destination = firstPostDestination(gateWith(flags));
      if (destination === "takeover") void navigate({ to: "/locations/$locationId/get-started", params });
      if (destination === "post-form") {
        void navigate({ to: "/locations/$locationId/schedule", params, search: { post_gig_type: "single" } });
      }
    });
  }, [isInitialGigPost, locationId, isStripeReturn, queryClient, gateWith, navigate]);
};
